import { useCallback, useState } from 'react';
import { IconPhoto } from '@tabler/icons-react';
import { cx } from '../utils/cx.js';
import { Tooltip } from './Tooltip.js';
import { SegmentedControl } from './SegmentedControl.js';
import { DocumentDropzone } from './DocumentDropzone.js';
import { WP_IMAGE_FILE_ACCEPT } from './FilePickerField.js';
import { useClipboardImagePaste } from '../utils/useClipboardImagePaste.js';
import styles from './GalleryImagePickerPanel.module.css';
import { Pressable } from './Pressable.js';

export type GalleryImagePickerItem = {
  id: string;
  url: string | null;
  label?: string;
  disabled?: boolean;
  disabledReason?: string;
};

export type GalleryImagePickerMode = 'gallery' | 'upload';

export type GalleryImagePickerPanelProps = {
  className?: string;
  items: GalleryImagePickerItem[];
  selectedId?: string | null;
  onSelect: (id: string) => void;
  onUpload: (file: File) => void;
  uploadLabel?: string;
  /** Shown in dropzone hint; paste still works via Ctrl+V. */
  pasteLabel?: string;
  emptyLabel?: string;
  /** Narrow grid for thumbnail popover. */
  compact?: boolean;
  disabled?: boolean;
  accept?: string;
  /** Initial mode. Default: gallery when items exist, else upload. */
  defaultMode?: GalleryImagePickerMode;
};

const MODE_OPTIONS: { value: GalleryImagePickerMode; label: string }[] = [
  { value: 'gallery', label: 'Galerie' },
  { value: 'upload', label: 'Téléverser' },
];

export function GalleryImagePickerPanel({
  className,
  items,
  selectedId = null,
  onSelect,
  onUpload,
  uploadLabel = 'Téléverser une image',
  pasteLabel = 'coller (Ctrl+V)',
  emptyLabel = 'Aucune image dans la galerie.',
  compact = false,
  disabled = false,
  accept = WP_IMAGE_FILE_ACCEPT,
  defaultMode,
}: GalleryImagePickerPanelProps) {
  const [mode, setMode] = useState<GalleryImagePickerMode>(
    () => defaultMode ?? (items.length > 0 ? 'gallery' : 'upload'),
  );

  const commitUpload = useCallback(
    (file: File) => {
      onUpload(file);
      setMode('gallery');
    },
    [onUpload],
  );

  const { wrapProps } = useClipboardImagePaste({
    disabled: disabled || mode !== 'upload',
    accept,
    extraActive: mode === 'upload',
    onFile: commitUpload,
  });

  const uploadHint = `Glissez-déposez une image ici, cliquez pour parcourir, ou ${pasteLabel}.`;

  const renderTile = (item: GalleryImagePickerItem) => {
    const isSelected = selectedId === item.id;
    const isDisabled = disabled || Boolean(item.disabled);
    const tile = (
      <Pressable
        type="button"
        role="option"
        aria-selected={isSelected}
        aria-disabled={isDisabled}
        disabled={isDisabled}
        className={cx(
          styles.tile,
          isSelected && styles.tileSelected,
          item.disabled && styles.tileDisabled,
        )}
        onClick={() => {
          if (isDisabled) return;
          onSelect(item.id);
        }}
      >
        <span className={styles.tileMediaShell}>
          {item.url ? (
            <img src={item.url} alt="" className={styles.tileMedia} />
          ) : (
            <span className={styles.tilePlaceholder} aria-hidden>
              <IconPhoto size={compact ? 16 : 20} stroke={1.25} />
            </span>
          )}
        </span>
        <span className={styles.tileLabel} title={item.label?.trim() || undefined}>
          {item.label?.trim() || '\u00a0'}
        </span>
      </Pressable>
    );

    const shell = (
      <div key={item.id} className={styles.tileShell}>
        {tile}
      </div>
    );

    if (item.disabled && item.disabledReason) {
      return (
        <Tooltip key={item.id} label={item.disabledReason} position="bottom" portal>
          {shell}
        </Tooltip>
      );
    }

    return shell;
  };

  return (
    <div className={cx(styles.panel, compact && styles.panelCompact, className)} {...wrapProps}>
      <div className={styles.modeSwitch}>
        <SegmentedControl
          value={mode}
          onChange={setMode}
          options={MODE_OPTIONS}
          aria-label="Source d'image"
          variant="tabs"
        />
      </div>

      {mode === 'gallery' ? (
        <div
          className={cx(styles.gridScroll, !compact && styles.gridScrollExpanded)}
          role="listbox"
          aria-label="Galerie"
        >
          {items.length === 0 ? (
            <p className={styles.empty}>
              {emptyLabel}
              <button
                type="button"
                className={styles.emptySwitch}
                disabled={disabled}
                onClick={() => setMode('upload')}
              >
                Téléverser une image
              </button>
            </p>
          ) : (
            <div className={cx(styles.grid, compact && styles.gridCompact)}>
              {items.map(renderTile)}
            </div>
          )}
        </div>
      ) : (
        <DocumentDropzone
          compact={compact}
          accept={accept}
          disabled={disabled}
          heading={uploadLabel}
          hint={uploadHint}
          aria-label={uploadLabel}
          onFileChange={(file) => {
            if (file) commitUpload(file);
          }}
        />
      )}
    </div>
  );
}
